import type {
	BuildTimelineResult,
	CalculationResult,
	ThermalInputs,
} from "../../engines/types";
import type { Dictionary } from "../../i18n";
import type { WallOpening } from "../framing/generateFramedWall";
import type { Market } from "../location/types";
import type { ToolGroup } from "./toolLibrary";

export type BuildGuideDictionaryKey = Extract<
	keyof Dictionary,
	`domain_build_${string}`
>;
export type BuildGuideTextArgs = Record<string, string | number>;

export interface BuildGuideTextToken {
	key: BuildGuideDictionaryKey;
	args?: BuildGuideTextArgs;
}

/**
 * A bill-of-materials row quoted in the guide. Kept as its fields, not joined
 * into one string, so each field is translated the way the Materials tab
 * translates it — a joined "material dimension • quantity" has no reliable
 * boundary between the material and its dimension.
 */
export interface BuildGuideMaterialRow {
	materialRow: { material: string; dimension: string; quantity: string };
}

/** A cut-list row quoted in the guide, translated like the Cut list tab. */
export interface BuildGuideCutRow {
	cutRow: { part: string; count: string; cutLength: string };
}

export type BuildGuideText =
	| string
	| BuildGuideTextToken
	| BuildGuideMaterialRow
	| BuildGuideCutRow;

/** The engine's own (Swedish) wording of a row, as the guide always joined it. */
export function formatGuideRow(
	value: BuildGuideMaterialRow | BuildGuideCutRow,
): string {
	if ("materialRow" in value) {
		const { material, dimension, quantity } = value.materialRow;
		return `${material} ${dimension} • ${quantity}`;
	}
	const { part, count, cutLength } = value.cutRow;
	return `${part} • ${count} • ${cutLength}`;
}

export function isGuideRow(
	value: BuildGuideText,
): value is BuildGuideMaterialRow | BuildGuideCutRow {
	return (
		typeof value === "object" && ("materialRow" in value || "cutRow" in value)
	);
}

export interface BuildGuideCheckpointDraft {
	id: string;
	label: BuildGuideText;
	detail?: BuildGuideText;
}

export interface BuildGuideSubstepDraft {
	id: string;
	title: BuildGuideText;
	purpose?: BuildGuideText;
	checkpoints: BuildGuideCheckpointDraft[];
	crew?: {
		size: "solo" | "duo";
		reason: BuildGuideTextToken;
	};
	mistakeCallout?: {
		severity: "safety" | "quality";
		wrong: BuildGuideText;
		right: BuildGuideText;
	};
	tools?: ToolGroup[];
	materialsSummary?: BuildGuideText[];
	measurements?: BuildGuideText[];
}

export interface ProjectBuildGuideStepDraft {
	id: string;
	title: BuildGuideText;
	purpose: BuildGuideText;
	prerequisites: BuildGuideText[];
	substeps: BuildGuideSubstepDraft[];
	materialsSummary: BuildGuideText[];
	cutSummary: BuildGuideText[];
	risks: BuildGuideText[];
	controls: BuildGuideText[];
	tools: ToolGroup[];
	dependencyIds: string[];
	timeline?: BuildTimelineResult["steps"][number];
	professionalBadge: string;
	professionalReason: string;
}

export interface BuildGuideModifierContext {
	data: CalculationResult;
	width: number;
	length: number;
	openings: WallOpening[];
	thermalInputs: ThermalInputs;
	market: Market;
}

export type BuildGuideStepModifier = (
	steps: ProjectBuildGuideStepDraft[],
	context: BuildGuideModifierContext,
) => ProjectBuildGuideStepDraft[];

export function buildGuideText(
	key: BuildGuideDictionaryKey,
	args?: BuildGuideTextArgs,
): BuildGuideTextToken {
	return { key, args };
}

/**
 * Insert substeps immediately after the substep whose id is `anchorId`.
 *
 * Engine guide modifiers use this instead of fixed array indices so a change to
 * the shared framing/insulation/… substep order can't silently move where an
 * engine's extra content lands (same robustness principle as gates anchoring on
 * `blocksStepId`). If the anchor id is not present the new substeps are appended
 * at the end — content is never dropped, it just loses its precise position.
 */
export function insertSubstepsAfterId(
	substeps: BuildGuideSubstepDraft[],
	anchorId: string,
	toInsert: BuildGuideSubstepDraft[],
): BuildGuideSubstepDraft[] {
	if (toInsert.length === 0) return substeps;
	const anchorIndex = substeps.findIndex((substep) => substep.id === anchorId);
	if (anchorIndex === -1) {
		return [...substeps, ...toInsert];
	}
	return [
		...substeps.slice(0, anchorIndex + 1),
		...toInsert,
		...substeps.slice(anchorIndex + 1),
	];
}
